import type { PropType } from 'vue'
import Play from './play.vue'
import style from './media.module.scss'
import type { Template } from '~/api/interface'
import Resource from '~/components/resource'
import { useTemplateDetail } from '~/hooks/useModal/modals/template'
import { getTagIDs } from '~/features/template'

const bgUrl = transformImage(getFeUrl('/verticalBg.png'))

export default defineComponent({
  props: {
    template: {
      type: Object as PropType<Template>,
      required: true,
    },
  },
  emits: ['reload'],
  setup(props, { emit }) {
    const vertical = ref(false)
    const resourceRef = ref()
    const isPlaying = ref(false)
    const mounted = ref(false)
    const poster = ref('')
    const posterUsed = ref(false)

    function onPlay() {
      isPlaying.value = true
      posterUsed.value = true
    }

    function onPause() {
      isPlaying.value = false
    }

    function reload() {
      emit('reload')
    }

    function segVertical(video: HTMLVideoElement) {
      const originWidth = video.videoWidth || 0
      const originHeight = video.videoHeight || 0

      if (originHeight > originWidth) {
        vertical.value = true
      } else {
        vertical.value = false
      }
    }

    const resourceLocation = injectResourceLocation()

    const userInfo = useUserInfo()

    function showTemplateDetail() {
      trackEvent(TRACK_EVENT.CLICK_TEMPLATE_PREVIEW, {
        template_id: props.template.id,
        template_title: props.template.name,
        source: resourceLocation?.source,
        tags: getTagIDs(props.template.tag_ids).map((item) => item.tag_name),
        org_id: userInfo.value?.organization_id,
      })

      useTemplateDetail(
        [],
        props.template,
        { source: resourceLocation?.source },
        reload
      )
    }

    let version = 0

    const url = props.template.preview_video

    watch(
      [() => props.template.preview_video, () => props.template.hover_video],
      () => {
        version++

        checkVideoDimension(version)
      },
      { immediate: true }
    )

    function checkVideoDimension(oldVersion: number) {
      if (import.meta.server) {
        return
      }

      const video = document.createElement('video')
      video.src = url
      video.controls = true
      document.body.appendChild(video)
      video.setAttribute('style', 'position: absolute; top: 0; left: -9999px;')
      video.addEventListener('loadedmetadata', () => {
        segVertical(video)
        mounted.value = true
        if (oldVersion !== version) {
          document.body.removeChild(video)
          return
        }

        poster.value = props.template.cover_oss_path
          ? transformImage(getBeUrl('/' + props.template.cover_oss_path), {
              width: video.videoWidth,
              height: video.videoHeight,
            })
          : ''

        poster.value = props.template.cover_url
          ? transformImage(props.template.cover_url, {
              width: video.videoWidth,
              height: video.videoHeight,
            })
          : poster.value

        document.body.removeChild(video)
      })
    }

    return () => {
      if (!mounted.value) {
        return
      }

      if (vertical.value) {
        return (
          <ClientOnly>
            <Resource
              class={style.verticalBg}
              src={bgUrl}
              onMouseover={() => resourceRef.value.play()}
              onMouseout={() => resourceRef.value.pause()}
            />
            <Resource
              ref={resourceRef}
              src={url}
              class={style.verticalResource}
              loop
              muted
              poster={poster.value}
              onPlay={onPlay}
              onPause={onPause}
              onMouseover={() => resourceRef.value.play()}
              onMouseout={() => resourceRef.value.pause()}
              onClick={showTemplateDetail}
            />
            {isPlaying.value ? null : (
              <Play class={[style.videoAction, 'ml-[14px]'].join(' ')}></Play>
            )}
          </ClientOnly>
        )
      }

      return (
        <ClientOnly>
          <Resource
            ref={resourceRef}
            class={style.normalResource}
            src={url}
            loop
            muted
            onPlay={onPlay}
            poster={poster.value}
            onPause={onPause}
            onMouseover={() => resourceRef.value.play()}
            onMouseout={() => resourceRef.value.pause()}
            onClick={showTemplateDetail}
          />
          {isPlaying.value ? null : <Play class={style.videoAction}></Play>}
        </ClientOnly>
      )
    }
  },
})
